import { BackdropCircularProgress, EnhancedDropDownRHF, EnhancedDropDownRHFWithLabel } from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { IAppState } from "../../../store/reducers";
import { IDropDownListResponse } from "../../../models/common/IDropDownListResponse";
import { ReportsService } from "../../../services/ReportsService";
import { UnchargedTransactionDataTable } from "./UnchargedTransactionDataTable";
import { setSelectedActivityLogActions } from "../../../store/actions/activityLogActions";
import { setSelectedDrawerOptionsMenu } from "../../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../../store/actions/stickyNavBarActions";
import { sortDDLByLabel } from "../../../common/Common";

export interface IInvoiceUnchargedTransaction { }

const useUnchargedTransaction = (
  clientId: string,
  taxYear: number,
  type: any,
) =>
  useQuery({
    queryKey: ["Uncharged-Transaction", clientId, taxYear, type],
    queryFn: (data) => {
      return ReportsService.getInvoiceUnchargedTransaction(clientId, taxYear, type);

    },
    refetchOnWindowFocus: false
  });

const useGetTransactionTypeDDL = (clientId: string, taxYear: number) =>
  useQuery({
    queryKey: ["transactions-type-ddl"],
    queryFn: (data) => {
      return ReportsService.getTransactionTypeDDL(clientId, taxYear);
    },
    refetchOnWindowFocus: false,
  });
export const InvoiceUnchargedTransaction: React.FC<IInvoiceUnchargedTransaction> = () => {
  const theme = useTheme();
  const queryClient = useQueryClient();
  // const navigate = useNavigate();
  const dispatch = useDispatch();
  const clientId = useSelector((state: IAppState) => state.client.id);
  const clientName = useSelector((state: IAppState) => state.client.clientName);
  const taxYear = useSelector((state: IAppState) => state.taxYear.year);
  const refNo = useSelector((state: IAppState) => state.invoice.refNo);
  const [selectedTransactionType, setSelectedTransactionType] = useState('all')

  const formMethods = useForm<any>({
    // resolver: yupResolver(ValidationSchema),
    mode: "onBlur",
    reValidateMode: "onChange",
  });

  const { isPending: isPendingTransactionTypeDDL, data: transactionTypeDDL } = useGetTransactionTypeDDL(clientId, taxYear);
  const transactionsTypeDDLItems = useMemo(() => {
    if (transactionTypeDDL) {
      if (
        transactionTypeDDL &&
        Array.isArray(transactionTypeDDL)
      ) {
        let resources: IDropDownListResponse[] =
          sortDDLByLabel(transactionTypeDDL).map(
            (resourceItem: IDropDownListResponse) => ({
              // id: resourceItem.id,
              label: resourceItem.label,
              value: resourceItem.value,
            })
          );

        resources.unshift({
          label: "All",
          value: "all",
        });

        return resources;
      }
      return [];
    }
  }, [transactionTypeDDL]);


  const { isLoading: isLoadingUnchargedTransactionList, data } = useUnchargedTransaction(clientId, taxYear, 'all'); //useQuery('accountants-list', AccountantService.getAccountantList, refetchOnWindowFocus: false);
  const unchargedTransactionData: any = data;
  const drawerMenuOptions = useSelector(
    (state: IAppState) => state.drawerMenu.menu
  );
  const expandedLabels = ["Reports", "Accounting"];
  const expandedSalaryProfileListOrSetupItem = "Invoice";
  const enhanceData = (data: any[], parentLabel?: string): any[] => {
    return data.map((item) => {
      let isExpanded = expandedLabels.includes(item.label);
      if (
        parentLabel === "Accounting" &&
        item.label === expandedSalaryProfileListOrSetupItem
      ) {
        isExpanded = true;
      }
      const enhancedItem = {
        ...item,
        expanded: item.items ? isExpanded : false,
        items: item.items ? enhanceData(item.items, item.label) : undefined,
        path: item.label === expandedSalaryProfileListOrSetupItem && "/reports/invoice",
      };
      return enhancedItem;
    });
  };
  const onSubmit = (values: any) => { };
  const onTransactionTypeSelectionChangeHandler = (selectedItem: any) => {
    setSelectedTransactionType(selectedItem.value)
    queryClient.invalidateQueries({
      queryKey: [
        "Uncharged-Transaction",
        clientId,
        taxYear,
        selectedItem.value,
      ],
    });
  };
  useEffect(() => {
    dispatch(setSelectedActivityLogActions({
      entityId1: '',
      entityId2: '',
      entityId3: '',
      screenName: '',
      companyName: '',
      activityType: "",
      activityLogVisibility: false
    }));
    dispatch(setSelectedStickyNavBar({ isSticky: false }));
    if (drawerMenuOptions && drawerMenuOptions.length > 0) {
      const enhancedData = enhanceData(drawerMenuOptions);
      dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
    }
  }, [])

  return (
    <>
      <div
        style={{
          margin: "15px 0",
          fontSize: "1.5rem",
          fontWeight: "bold",
          fontFamily: "'Roboto','Helvetica','Arial',sans-serif",
        }}
      >
        {"Invoice Uncharged Transactions"}
      </div>
      <FormProvider {...formMethods}>
        <form onSubmit={formMethods.handleSubmit(onSubmit)}>
          <Grid size={{ xs: 12, sm: 8, md: 8, lg: 8, xl: 8 }}>
            <Grid
              container
              size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
              sx={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderBottom: 0,
                borderRadius: "2px 2px 0 0",
                padding: "13px 20px 20px 20px",
              }}
            >
              <Grid
                size={{ xs: 12, sm: 4, md: 2, lg: 2, xl: 2 }}
                sx={{ "& .dropDownWrapper": { marginBottom: "0!important" } }}
              >
                <EnhancedDropDownRHFWithLabel
                  selectName="transactionType"
                  label="Transaction Type"
                  testId={`type-list`}
                  items={transactionsTypeDDLItems ?? []}
                  selectedItem={selectedTransactionType}
                  onSelectionChangeHandler={onTransactionTypeSelectionChangeHandler}
                />
              </Grid>
              <Grid
                size={{ xs: 12, sm: 2, md: 1, lg: 1, xl: 1 }}
                sx={{ marginTop: "auto", marginLeft: "5px" }}
              >
              </Grid>
              <Grid size={{ xs: 0, sm: 0, md: 0.5, lg: 0.5, xl: 0.5 }}></Grid>
              <Grid
                size={{ xs: 12, sm: 3, md: 1.5, lg: 1.5, xl: 1.5 }}
                sx={{ color: "rgba(0, 0, 0, 0.6)", fontSize: "0.875rem" }}
              >
              </Grid>
              <Grid
                size={{ xs: 0, sm: 0, md: 4.95, lg: 4.95, xl: 4.95 }}
              ></Grid>
              <Grid
                size={{ xs: 12, sm: 3, md: 2, lg: 2, xl: 2 }}
                sx={{
                  textAlign: "right",
                  color: "rgba(0, 0, 0, 0.6)",
                  fontSize: "0.875rem",
                }}
              >
              </Grid>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
      <UnchargedTransactionDataTable
        rows={unchargedTransactionData && unchargedTransactionData.items}
        last_rows={unchargedTransactionData && unchargedTransactionData.sumData}
        columns={unchargedTransactionData && unchargedTransactionData.headerColumns}
        showIndexColumn={true}
        showChecboxColumn={false}
        showSettingColumn={false}
        isLoading={isLoadingUnchargedTransactionList}
      />
    </>
  );
};
